import { useFormikContext } from 'formik';
import { useState } from 'react';

import { useMutateSuggestion } from 'hb-react/shared/components/AIFormWidget/hooks/useMutateSuggestion';
import {
  type AIFormWidgetFormValues,
  type FieldSchema,
  type FieldSuggestion,
  type ParentFormikFormValues,
  type UpdateSuggestionAction,
} from 'hb-react/shared/components/AIFormWidget/types';
import {
  filterSensitiveFields,
  hasAnalyzableContent,
} from 'hb-react/shared/components/AIFormWidget/utils';

import {
  createDefaultFieldSchemas,
  extractTargetData,
  processSuggestions,
} from './utils';

/**
 * Hook for AI Form Widget functionality
 *
 * @template TFormValues - Type of the parent Formik form values (must extend ParentFormikFormValues, defaults to ParentFormikFormValues)
 * @param enabledFields - Array of field schemas that can be enhanced by AI
 * @returns AI Form Widget state and actions
 */
export const useAIFormWidget = <
  TFormValues extends ParentFormikFormValues = ParentFormikFormValues,
>({
  enabledFields,
}: {
  enabledFields: Array<FieldSchema>;
}) => {
  const parentFormikContext = useFormikContext<TFormValues>();

  const [suggestions, setSuggestions] = useState<Array<FieldSuggestion>>([]);

  const { mutateAsync } = useMutateSuggestion();

  const canAnalyze = hasAnalyzableContent(parentFormikContext.values);

  const onSubmit = async ({ aiPrompt }: AIFormWidgetFormValues) => {
    if (!canAnalyze || !aiPrompt.trim()) {
      return;
    }

    try {
      const currentValues = parentFormikContext.values;
      const filteredValues = filterSensitiveFields(currentValues);

      const targetFields =
        enabledFields || createDefaultFieldSchemas(filteredValues);
      const targetData = extractTargetData(filteredValues, targetFields);

      const { suggestions: apiSuggestions } = await mutateAsync({
        prompt: aiPrompt,
        currentValues: targetData,
        targetFields,
      });

      const fullSuggestions = processSuggestions(
        apiSuggestions,
        targetFields,
        targetData,
      );
      setSuggestions(fullSuggestions);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get AI suggestions:', error);
      // TODO: Show error message to user
    }
  };

  const updateSuggestion: UpdateSuggestionAction = (suggestion) => {
    setSuggestions((prev) =>
      prev.map((s) => (s.id === suggestion.id ? { ...s, ...suggestion } : s)),
    );
  };

  const acceptSuggestion = (suggestion: FieldSuggestion) => {
    parentFormikContext.setFieldValue(
      suggestion.fieldName,
      suggestion.suggestedValue,
    );

    updateSuggestion({
      id: suggestion.id,
      status: 'accepted' as const,
    });
  };

  const rejectSuggestion = (id: string) =>
    updateSuggestion({ id, status: 'rejected' as const });

  const acceptAllSuggestions = () => {
    const pendingSuggestions = suggestions.filter(
      (suggestion) => suggestion.status === 'pending',
    );

    pendingSuggestions.forEach((suggestion) => {
      // Apply suggestion to Formik
      parentFormikContext.setFieldValue(
        suggestion.fieldName,
        suggestion.suggestedValue,
      );
      // Update suggestion status
      updateSuggestion({
        id: suggestion.id,
        status: 'accepted' as const,
      });
    });
  };

  const resetSuggestions = () => {
    setSuggestions([]);
  };

  const initialValues: AIFormWidgetFormValues = {
    aiPrompt: '',
  };

  return {
    onSubmit,
    suggestions,
    setSuggestions,
    isEnabled: canAnalyze,
    initialValues,
    updateSuggestion,
    acceptSuggestion,
    rejectSuggestion,
    acceptAllSuggestions,
    resetSuggestions,
    parentFormikContext,
  };
};
